import './DatasourceExplorer.scss';
import { Flex, Segmented, Button, Tooltip, Select } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import AppSearch from '../common/AppSearch';
import { BdaDataset } from '../../models/BdaDataset';
import { BdaModel } from '../../models/BdaModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Datasource, ObjectStorageDatasource } from '../../models/Datasource';
import { useToasts } from '../../contexts/toasts';
import { fixDirName, normalizePath, setObjectType } from '../../utils';
import { QueryDetails, VizClient } from '../../api/VizClient';
import classNames from 'classnames';
import { DatasourceListItem } from '../forms/AssetEditForm';
import AlidaLoader from '../common/AlidaLoader';
import { Datasources } from '../../api/Datasources';
import UploadModal from './UploadModal';
import IconButton from '../common/IconButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import ScriptEditor from '../common/ScriptEditor';
import AppLoader from '../common/AppLoader';

const typeOptions = [
  { value: "table", icon: <Tooltip title="Table" mouseEnterDelay={0.5}><FontAwesomeIcon icon={solid('table')} /></Tooltip> },
  { value: "generic", icon: <Tooltip title="Generic" mouseEnterDelay={0.5}><FontAwesomeIcon icon={solid('photo-film')} /></Tooltip> }
];

interface TreeElement {
  name: string;
  csvFilesPresent?: boolean;
  elements?: TreeElement[];
  isDir?: boolean;
  selected?: boolean;
}

type Props = {
  asset?: Partial<BdaDataset | BdaModel>;
  type: string;
  onTypeChange: React.Dispatch<React.SetStateAction<string>>;
  mode: 'dataset' | 'model';
  validated?: boolean;
  table: any;
  onTableChange: React.Dispatch<React.SetStateAction<any>>;
  datasource?: Datasource;
  onDatasourceChange: React.Dispatch<React.SetStateAction<Datasource | undefined>>;
  onPathChange: (parts: string[], containsDataset?: boolean) => void;
  onDetailsChange?: (value: string | undefined) => void;
}

export default function DatasourceExplorer({
  asset, mode, validated, onDetailsChange,
  type, onTypeChange, table, onTableChange,
  datasource, onDatasourceChange, onPathChange,
}: Props) {
  const datasourceContainer = useRef<HTMLDivElement | null>(null);
  const [search, setSearch] = useState('');
  const [datasourceSearch, setDatasourceSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [directoryTree, setDirectoryTree] = useState<TreeElement[]>();
  const [datasources, setDatasources] = useState<Datasource[]>();
  const [path, setPath] = useState<string[]>([]);
  const [tables, setTables] = useState<any[]>();
  const [error, setError] = useState(false);

  const { showToast } = useToasts();

  const filteredTree = useMemo(() => directoryTree?.filter(el => el.name.toLowerCase().includes(search.toLowerCase())), [directoryTree, search]);
  const showSelectPathButton = useMemo(() => datasource && path.length > 1 && path.join('/') !== (asset as BdaDataset).datasetFileType?.path, [datasource, asset, path]);

  const datasourceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasources"));

  const initDatasources = useCallback(() => {
    Datasources.getDatasources()
      .then(
        ({ data }) => setDatasources(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasources-error', message, `Datasources Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  useEffect(initDatasources, [initDatasources]);

  useEffect(() => {
    if (path.length === 0 && asset?.id) {
      // const path = ((asset as BdaDataset).datasetFileType!.tableName || (asset as BdaDataset).datasetFileType!.path) + "/";
      let path = "";
      if (mode === "model" && setObjectType<BdaModel>(asset) && setObjectType<ObjectStorageDatasource>(asset.datasource))
        path = (asset.datasource.prefixPath ? asset.datasource.prefixPath + "/" : "") + asset.location;
      else if (setObjectType<BdaDataset>(asset) && setObjectType<ObjectStorageDatasource>(asset.datasource))
        path = (asset.datasource.prefixPath ? asset.datasource.prefixPath + "/" : "") + (asset.datasetFileType!.tableName || asset.datasetFileType!.path) + "/";
      setPath(normalizePath(path!).split('/').slice(0, -1));
    }
  }, [asset, path, mode]);

  useEffect(() => {
    if (asset?.id && datasources && datasource) {
      const selectedDatasource = document.querySelector(".explorer-sidebar div.datasource-list-item.selected") as HTMLButtonElement;
      if (selectedDatasource) datasourceContainer.current!.scrollTop = selectedDatasource!.offsetTop - datasourceContainer.current!.offsetTop;
      if (selectedDatasource) datasourceContainer.current!.scrollTo({ top: selectedDatasource!.offsetTop - datasourceContainer.current!.offsetTop });
    }
  }, [asset, datasources, datasource]);

  const getTables = useCallback(() => {
    const body = new QueryDetails(datasource);
    const isMessageBroker = datasource!.type === 'messageBroker';

    const promise = isMessageBroker
      ? VizClient.getTopics(body)
      : VizClient.getTables(body);

    promise.then(
      ({ data }) => setTables(data || []),
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('tables-error', message, `${isMessageBroker ? 'Topics' : 'Tables'} Error ${status ? `(${status})` : ''}`, 'danger');
        setError(true);
      },
    )
  }, [datasource, showToast]);

  const getDirectoryTree = useCallback((parts: string[]) => {
    const body = new QueryDetails(datasource);
    body.path = normalizePath(parts.join('/') || body.prefixPath || "");

    VizClient.browseDirectories(body)
      .then(
        ({ data }) => setDirectoryTree(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('directories-error', message, `Browsing Error ${status ? `(${status})` : ''}`, 'danger');
          setDirectoryTree([]);
        }
      )
  }, [datasource, showToast]);

  const refreshTree = useCallback(() => {
    setDirectoryTree(undefined);
    getDirectoryTree(path);
  }, [getDirectoryTree, path])

  useEffect(() => {
    setDirectoryTree(undefined);
    if (datasource) {
      if (datasource.type === "remote") return;
      if (datasource.type === "objectStorage") getDirectoryTree(path);
      else getTables();
    }
  }, [datasource, getDirectoryTree, getTables, path]);

  const openUploadModal = useCallback(() => setShowModal(true), []);

  const handleSelectPath = useCallback(() => {
    const containsDataset = directoryTree?.some(el => el.csvFilesPresent);
    onPathChange(path, containsDataset);
  }, [onPathChange, path, directoryTree]);

  const handleNavigateBack = useCallback(() => {
    setPath(path => path.slice(0, -1));
  }, []);

  const navigateToPart = useCallback((index: number) => () => {
    setPath(path => path.slice(0, index + 1));
  }, []);

  const handleSelectDatasource = useCallback((datasource: Datasource) => () => {
    setError(false);
    setTables(undefined);
    onTableChange(null);
    // TODO is this needed?
    // setPath(datasource.prefixPath ? [fixDirName(datasource.prefixPath)] : ['']);
    onDatasourceChange(datasource);
    if (datasource.type === 'objectStorage' && setObjectType<ObjectStorageDatasource>(datasource)) setPath(datasource.prefixPath ? [fixDirName(datasource.prefixPath)] : []);
    if (datasource.type === 'remote') onTypeChange('endpoint');
  }, [onDatasourceChange, onTableChange, onTypeChange]);

  const handleSelectTable = useCallback((tableName: string) => {
    const table = tables!.find(table => table.name === tableName);
    onTableChange(table);
  }, [tables, onTableChange]);

  const handleChangeDirectory = useCallback((element: TreeElement) => () => {
    if (!element.isDir) return;
    setSearch('');
    // TODO no need to fix dir?
    // setPath(path => path.concat(fixDirName(element.name)));
    setPath(path => path.concat(element.name));
  }, []);

  const handleModalClose = useCallback(() => setShowModal(false), []);

  const renderDatasourceContent = useCallback(() => {
    if (datasource!.type === "remote") return (
      <div className="m-2">
        <Form.Group controlId="dataset-details">
          <Form.Label>Details</Form.Label>
          <ScriptEditor value={(asset as BdaDataset).datasetFileType?.details} onChange={onDetailsChange} height={250} />
        </Form.Group>
      </div>
    )

    if (datasource!.type !== "objectStorage") return (
      <div className="m-2">
        <Form.Label>{datasource?.type === 'messageBroker' ? 'Topic' : 'Table'}</Form.Label>
        <Select
          showSearch
          allowClear
          loading={!tables}
          className="w-100"
          placeholder={`Choose ${datasource?.type === 'messageBroker' ? 'Topic' : 'Table'}`}
          status={(validated && !table) ? 'error' : undefined}
          optionFilterProp="label"
          onChange={handleSelectTable}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={tables?.map(table => ({
            label: table.name,
            value: table.name,
          }))}
          value={table?.name}
        />
      </div>
    )

    if (!filteredTree) return <AppLoader className="py-5" justify="center" size={80} />

    return (
      <div className="tree-wrapper">
        {filteredTree?.length === 0 && (
          <Alert variant="info" className="py-1 m-1">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">No elements found.</span>
          </Alert>
        )}
        {filteredTree?.map(el => (
          <Flex justify="start" key={el.name}>
            <div className={classNames("tree-element", { dir: el.isDir })} onClick={handleChangeDirectory(el)}>
              <FontAwesomeIcon icon={el.isDir ? solid('folder') : solid('file')} />
              <span className="ms-2">{el.name}</span>
              {el.csvFilesPresent && (
                <Tooltip placement="right" title={el.isDir ? "Folder contains tabular data" : "File represents tabular data"}>
                  <FontAwesomeIcon icon={solid('table')} className="ms-2" />
                </Tooltip>
              )}
            </div>
          </Flex>
        ))}
      </div>
    )
  }, [filteredTree, handleChangeDirectory, handleSelectTable, table, datasource, tables, validated, asset, onDetailsChange]);

  const renderAsyncContent = useCallback(() => {
    if (!datasource) return (
      <Flex align="center" justify="center" className="bg-light h-100 text-muted">
        <FontAwesomeIcon icon={solid('faucet-drip')} size="2x" />
        <h5 className="mb-0 ms-3">Choose one {datasourceLabels.singular} to see details here.</h5>
      </Flex>
    );

    if (error) return (
      <Alert variant="danger" className="py-2 m-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred with the selected data source. Try again later</span>
      </Alert>
    );

    return renderDatasourceContent();
  }, [datasource, renderDatasourceContent, error, datasourceLabels]);

  return (
    <>
      <Row className="datasource-explorer gx-0 mx-0">
        <Col md={3}>
          <aside className="explorer-sidebar border-end" ref={datasourceContainer}>
            <div className="p-1 px-2 border-bottom">
              <AppSearch placeholder="Search datasource..." value={datasourceSearch} onChange={setDatasourceSearch} />
            </div>
            <div className="sidebar-content">
              {datasources
                ? datasources
                  .filter(datasource => datasource.name.toLowerCase().includes(datasourceSearch.toLowerCase()))
                  .map(d => (
                    <DatasourceListItem key={d.id} datasource={d} onClick={handleSelectDatasource(d)} selected={datasource?.id === d.id} />
                  ))
                : (
                  <div className="d-flex py-5 justify-content-center">
                    <AlidaLoader size={100} />
                  </div>
                )
              }
            </div>
          </aside>
        </Col>
        <Col>
          <Flex vertical className="explorer-body">
            {datasource?.type !== 'messageBroker' && datasource?.type !== 'database' && datasource?.type !== 'remote' && (
              <Flex className="explorer-toolbar" gap={8} align="center">
                <Col md={6}>
                  <Flex align="center" className={classNames("path-input border rounded font-smaller", { 'bg-light': path.length === 0 })}>
                    {path.length > 1 && (
                      <Button type="text" size="small" onClick={handleNavigateBack}>
                        <FontAwesomeIcon icon={solid('arrow-left')} size="sm" />
                      </Button>
                    )}
                    <Col className="path-container">
                      {path.map((part, index) => {
                        const noTrailingSlashPart = (part.endsWith('/') ? part.slice(0, -1) : part);

                        return (
                          <div className="d-inline-block" key={index}>
                            {index === path.length - 1
                              ? <span className="px-1">{noTrailingSlashPart}</span>
                              : <Button type="text" size="small" className="fw-bold px-1" key={index} onClick={navigateToPart(index)}>{noTrailingSlashPart}</Button>
                            }
                            {index < path.length - 1 && <span style={{ margin: "0 0.125rem" }}>/</span>}
                          </div>
                        )
                      })}
                    </Col>
                    {showSelectPathButton && (
                      <IconButton type="button" tooltip="Select Path" onClick={handleSelectPath} className="p-1 me-1">
                        <FontAwesomeIcon icon={regular('check-circle')} size="sm" />
                      </IconButton>
                    )}
                    <IconButton type="button" tooltip="Upload files" onClick={openUploadModal} className="p-1">
                      <FontAwesomeIcon icon={solid('upload')} size="sm" />
                    </IconButton>
                  </Flex>
                </Col>
                <Col>
                  <AppSearch value={search} onChange={setSearch} />
                </Col>
                {mode === 'dataset' && (
                  <Segmented options={typeOptions} value={type} onChange={onTypeChange} />
                )}
              </Flex>
            )}
            <div className="explorer-content">{renderAsyncContent()}</div>
          </Flex>
        </Col>
      </Row>

      <UploadModal show={showModal} onClose={handleModalClose} selectedDatasource={datasource} selectedPath={normalizePath(path.join('/'))} onUploadCompleted={refreshTree} />
    </>
  )
}